// Pricing + cart-payload logic. Single source of truth for the customiser.
// All amounts in cents. All sums are derived from the active `spots` array
// + the live `Config` so there's no separate ledger to keep in sync.

import type { Config, CustomField, Placement, SizeTier, Upsell } from "./types";
import type { EmbroideryDraft } from "./sections/PlacementSection";
import { MATCH_DESIGN_CODE } from "./components/ThreadPicker";
import { wordArtPresetById } from "./wordart/presets";
import { flowKindFor } from "./flow";
import { evaluateCondition } from "./util/conditions";
import { previewOffsetProperty } from "./util/previewGeometry";

export type Spot = EmbroideryDraft & { id: string };

// Fallback constants used when Config doesn't carry the Template-derived
// fees. Templates created via the admin override these.
export const DIGITISATION_FEE_CENTS = 3000;
export const APPROVAL_FEE_CENTS = 1500;

export type ApprovalChoice = "approve" | "skip" | null;

// Phase 59b: the slug whitelist below is now LEGACY ONLY. Today every
// placement falls back to `config.sizeTiers` (the master ladder) when its
// own tier array is empty — see effectiveTiersFor below. The whitelist
// is kept as a safety net in case future code needs to identify which
// slugs were the canonical "tiered" ones before we made master tiers
// universal.
const TIERED_SLUGS = new Set([
  "cc",
  "centre-front",
  "centre-chest",
  "cb",
  "centre-back",
  "back",
  "full-back",
]);

export function isTieredPlacement(p: Placement, config?: Config): boolean {
  // Per-placement override always wins.
  if (Array.isArray(p.tiers) && p.tiers.length > 0) return true;
  // Phase 59b: any placement is tiered if a master ladder exists. The
  // master ladder lives on config.sizeTiers and reflects what the admin
  // set up on /app/master-tiers. This makes the master ladder a true
  // universal default — totes / hats / socks / new garments all get
  // priced unless the admin explicitly clears the master ladder for a
  // specific placement.
  if (config && Array.isArray(config.sizeTiers) && config.sizeTiers.length > 0) {
    return true;
  }
  return TIERED_SLUGS.has(p.slug.toLowerCase());
}

// Resolve the effective tier ladder for a placement at runtime. Returns
// the placement's per-template `tiers` array if non-empty (normalised to
// the SizeTier shape the slider expects); else falls back to the global
// Config.sizeTiers (the master ladder). Returns empty only when neither
// the placement nor the master ladder has tiers configured.
//
// Phase 63: ALWAYS clip the returned tier ladder to the placement's
// maxWidthMm. A left-chest placement capped at 90mm shouldn't surface
// the master ladder's 160/200/250/300mm rungs as pickable tiers —
// they're physically unreachable. We drop tiers whose minMm exceeds
// the cap, and clip the maxMm of the highest remaining tier so the
// slider's tier-cells, the band bar, and tierForSize all agree on the
// same picker domain.
export function effectiveTiersFor(p: Placement, config: Config): SizeTier[] {
  const cap = Number.isFinite(p.maxWidthMm) ? p.maxWidthMm : Infinity;
  let raw: SizeTier[] = [];
  if (Array.isArray(p.tiers) && p.tiers.length > 0) {
    // Sort ascending by upToMm so the picker's "first tier ≥ size" logic
    // works correctly regardless of admin entry order.
    const sorted = [...p.tiers].sort((a, b) => a.upToMm - b.upToMm);
    raw = sorted.map((t, i) => ({
      id: `${p.id}-tier-${i}`,
      minMm: i === 0 ? 0 : sorted[i - 1].upToMm + 1,
      maxMm: t.upToMm,
      label: t.label,
      priceCents: t.feeCents,
      stitchEstimate: 0,
    }));
  } else if (Array.isArray(config.sizeTiers) && config.sizeTiers.length > 0) {
    // Phase 59b: fall back to the master ladder for ANY placement.
    raw = config.sizeTiers;
  } else {
    return [];
  }
  // Phase 63: clip to the placement's maxWidthMm. Drop tiers that
  // start above the cap (entirely above), then clip the top tier's
  // maxMm down to the cap.
  const visible = raw.filter((t) => t.minMm <= cap);
  if (visible.length === 0) return [];
  return visible.map((t, i, arr) => ({
    ...t,
    maxMm: i === arr.length - 1 ? Math.min(t.maxMm, cap) : t.maxMm,
  }));
}

// Effective per-template fee helpers. Prefer Config-supplied values; fall
// back to the legacy constants for products that don't have a Template.

// Phase 68: "Free digitisation" promo. Single source of truth — every
// helper below routes through this. When true, the standard digitisation
// fee AND every upsell that REPLACES the digitisation step (pet portrait
// scan, kid drawing scan; identified by replacesStep === "logo-upload")
// resolve to $0. The original price stays available via the *OriginalFee*
// variants so the customer-facing UI can show "~~$30~~ Free".
export function isDigitisationFreePromo(config: Config): boolean {
  return config.settings?.digitisationFreePromo === true;
}

/** What the digitisation fee would be if the promo weren't active. Used
 *  for the strikethrough display in PriceBreakdown + the upload step. */
export function digitisationOriginalFeeFor(config: Config): number {
  return config.product.digitizationFeeCents ?? DIGITISATION_FEE_CENTS;
}

export function digitisationFeeFor(config: Config): number {
  if (isDigitisationFreePromo(config)) return 0;
  return digitisationOriginalFeeFor(config);
}

export function approvalFeeFor(config: Config): number {
  return config.product.approvalFeeCents ?? APPROVAL_FEE_CENTS;
}

// Phase 68: identifies upsells that ARE the digitisation step (pet
// portrait scan, kid drawing scan). When the free-digitisation promo is
// on, these upsells AND their per-count surcharges (which are all
// digitising work) go to $0. The "coloured + filled" style surcharge
// is excluded — it's an artwork-quality upgrade, not digitising work —
// so it stays at full price regardless of the promo.
function isLogoUploadReplacement(upsell: Upsell): boolean {
  return upsell.replacesStep === "logo-upload";
}

export function effectiveUpsellPriceCents(upsell: Upsell, config: Config): number {
  if (isDigitisationFreePromo(config) && isLogoUploadReplacement(upsell)) return 0;
  return upsell.priceCents;
}

export function effectiveUpsellPerCountCents(upsell: Upsell, config: Config): number {
  if (isDigitisationFreePromo(config) && isLogoUploadReplacement(upsell)) return 0;
  return upsell.perCountCents ?? 0;
}

export function effectiveUpsellStyleSurchargeCents(upsell: Upsell, _config: Config): number {
  // The "coloured + filled" style is an artwork-QUALITY upgrade, not a
  // digitisation step, so the free-digitisation promo must NOT discount
  // it. (The base scan fee + per-count fee ARE digitisation work and
  // stay discounted under the promo via the other two effectiveUpsell*
  // helpers.) Order RK23901 was undercharged because this used to also
  // zero — fixed 2026-05-24.
  return upsell.styleSurchargeCents ?? 0;
}

// 2026-08-14: "Match the design" threads carry the coloured + filled fee.
// Colour-matching the artwork at digitising IS the multi-colour fill work
// the "Coloured + filled in" style toggle charges $45 for — the logo flow
// offered it free while the drawing flows charged for the same stitching.
// Used when the spot has no style-governed add-on to source the fee from.
export const MATCH_DESIGN_SURCHARGE_CENTS = 4500;

// True when this spot's DESIGN threads carry the match sentinel. Text-only
// spots have no design to match, so mode gates the check the same way the
// cart-property emitter gates the "design threads" row.
function spotHasMatchDesignThreads(s: Spot): boolean {
  return (
    (s.mode === "logo" || s.mode === "both") &&
    (s.logoThreadCodes ?? []).includes(MATCH_DESIGN_CODE)
  );
}

// The fee "Match the design" adds to THIS spot, deduped against the style
// toggle: a spot whose add-on style axis is set to coloured pays NOTHING
// here — Phase 79 auto-fills the match sentinel on every coloured drawing
// spot, so without this dedupe every coloured pet/kid/person spot would
// double-charge. The dedupe keys on the style AXIS EXISTING
// (styleSurchargeCents != null), NOT on the fee being positive: zeroing
// the fee is the sanctioned "coloured included in the garment price"
// lever (see the RK23901/Phase-84/RK24263 note above) and must stay $0 —
// a surcharge-0 locked-coloured template must not sneak the $45 back in
// via the sentinel. The fee is per SPOT: two placements with matched
// threads each pay it (colour-matched fill is stitched per location),
// same per-location rule as the style toggle.
export function matchDesignSurchargeCentsFor(s: Spot, config: Config): number {
  if (!spotHasMatchDesignThreads(s)) return 0;
  if (s.addonUpsellId && s.addonStyleColoured) {
    const upsell = config.upsells?.find((u) => u.id === s.addonUpsellId);
    if (upsell && upsell.styleSurchargeCents != null) {
      return 0; // the add-on's style axis governs this spot's fee
    }
  }
  return MATCH_DESIGN_SURCHARGE_CENTS;
}

// Returns the additional-placement fee that applies to the Nth spot
// (1-indexed). First spot is always free. From 2nd onward, picks the row
// matching `nth`; for spots beyond the highest configured row, falls back
// to the last (= the "5th+" catch-all).
export function additionalPlacementFeeFor(
  config: Config,
  nth: number,
): number {
  const ladder = config.product.additionalPlacementFees;
  if (!Array.isArray(ladder) || ladder.length === 0) return 0;
  if (nth <= 1) return 0;
  const exact = ladder.find((f) => f.nth === nth);
  if (exact) return exact.feeCents;
  // Sort ascending by nth, then pick the highest entry whose nth ≤ N
  // (i.e. the last bracket the spot falls into).
  const sorted = [...ladder].sort((a, b) => a.nth - b.nth);
  const fallback = sorted.filter((f) => f.nth <= nth).pop() ?? sorted[sorted.length - 1];
  return fallback?.feeCents ?? 0;
}

export type TierSnapshot = { feeCents: number; label: string };

export function tierForSize(sizeMm: number, tiers: SizeTier[], tierMode: boolean): TierSnapshot {
  if (!tierMode) return { feeCents: 0, label: "Free" };
  if (!tiers.length) return { feeCents: 0, label: "Free" };
  for (const t of tiers) {
    if (sizeMm <= t.maxMm) return { feeCents: t.priceCents, label: t.label };
  }
  const last = tiers[tiers.length - 1];
  return { feeCents: last.priceCents, label: last.label };
}

// True if the spot has a logo (either Logo-only or Both modes).
function spotHasLogo(s: Spot): boolean {
  return (s.mode === "logo" || s.mode === "both") && !!s.uploadStagingKey;
}

// Failsafe: at most ONE digitisation charge per spot — a spot physically
// can't be digitised twice. A spot pays the STANDARD digitisation fee
// only when it has a real upload, hasn't been pre-paid (cross-order
// memory / ingested proof), AND isn't already covered by a pet/kid scan
// upsell. When `addonUpsellId` is set the upload step was replaced by a
// scan upsell ("Pet portrait digitisation" / "Kid's drawing scan") —
// that upsell IS the spot's digitisation, so the standard fee must not
// also apply.
function spotPaysStandardDigitisation(s: Spot): boolean {
  return (
    spotHasLogo(s) &&
    !!s.uploadStagingKey &&
    !s.uploadAlreadyDigitised &&
    !s.addonUpsellId
  );
}

// Phase 85: "first design free" digitisation. The FIRST unique design per
// garment has its digitisation included (the cost is baked into the
// garment price); each ADDITIONAL unique design pays its digitisation
// cost. A "design" is either:
//   - a pet/kid scan spot (addonUpsellId on a logo-upload-replacement
//     upsell, not already-digitised) -> cost = the scan upsell's base
//     priceCents (the scan IS that spot's digitisation), or
//   - a standard logo upload (deduped by uploadStagingKey) -> cost =
//     digitisationOriginalFeeFor(config) (the $30 fee).
// Reuse of one design across spots is a single event, so it stays free
// when it's the first. The blanket digitisationFreePromo still waives
// EVERY event when on (kept as a true "all free" sale lever).
//
// Single source of truth: totalCentsFor, subtotalForSpot and the
// propertiesFor cart breakdown all route through this so the headline
// total, the per-spot display and the order line items can never drift.
export type DigitisationEvent = {
  spot: Spot;
  kind: "scan" | "logo";
  label: string;
  cents: number; // cost when charged
  waived: boolean; // true => included (free)
};
export function digitisationEventsFor(
  spots: Spot[],
  config: Config,
): DigitisationEvent[] {
  const events: DigitisationEvent[] = [];
  // One set across BOTH kinds: an artwork (staging key) is digitised
  // once no matter how many spots stitch it, or which flow uploaded it.
  const seenKeys = new Set<string>();
  for (const s of spots) {
    // Pet/kid scan: the scan upsell IS this spot's digitisation.
    if (s.addonUpsellId && !s.uploadAlreadyDigitised) {
      const upsell = config.upsells?.find((u) => u.id === s.addonUpsellId);
      if (upsell && isLogoUploadReplacement(upsell)) {
        // 2026-08-21 (back spot reusing the front drawing charged +$30):
        // reuse of one artwork across spots is a single digitisation
        // event for scans too - the same staging key must never produce
        // a second scan event. The reuse tile also stamps
        // uploadAlreadyDigitised, so this dedupe is the pricing-level
        // backstop for drafts that toggled reuse -> new design -> reuse.
        // Keyless scan spots (upload-later) are distinct future artworks
        // and stay one event per spot.
        const key = s.uploadStagingKey;
        if (key && seenKeys.has(key)) continue;
        if (key) seenKeys.add(key);
        events.push({
          spot: s,
          kind: "scan",
          label: upsell.label || "Design digitisation",
          cents: upsell.priceCents,
          waived: false,
        });
        continue;
      }
    }
    // Standard logo upload, deduped by staging key (reuse = one event).
    if (spotPaysStandardDigitisation(s)) {
      const key = s.uploadStagingKey as string;
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        events.push({
          spot: s,
          kind: "logo",
          label: "Digitisation fee",
          cents: digitisationOriginalFeeFor(config),
          waived: false,
        });
      }
      continue;
    }
    // 2026-07-25: "I'll send it later" is still a real, new design that
    // will need digitising - the artwork just arrives after checkout.
    // Because it has no staging key it produced NO event at all, so it
    // neither consumed the free-first-design slot nor paid: an order of
    // [upload-later, new logo] handed over TWO digitisations for nothing,
    // and three upload-later spots lost $60. Scoped to the logo flow -
    // pet/kid/person upload-later spots carry an addonUpsellId and are
    // already covered by the scan branch above, so they must not be
    // charged a second time here.
    if (
      (s.mode === "logo" || s.mode === "both") &&
      s.uploadLater &&
      !s.uploadStagingKey &&
      !s.uploadAlreadyDigitised &&
      !s.addonUpsellId
    ) {
      events.push({
        spot: s,
        kind: "logo",
        label: "Digitisation fee",
        cents: digitisationOriginalFeeFor(config),
        waived: false,
      });
    }
  }
  if (isDigitisationFreePromo(config)) {
    for (const e of events) e.waived = true; // blanket promo: all free
  } else if (events.length > 0) {
    events[0].waived = true; // first design free
  }
  return events;
}

// Total cents added to the line item across all spots:
//   sum of every spot's tier fee (using per-placement tiers if defined,
//     else the global tier ladder for legacy tiered slugs)
//   + ONE digitisation fee per unique uploaded logo (so reusing a logo in
//     spot 2 doesn't double-charge)
//   + additional-placement fees from the 2nd spot onward (first is free)
//   + optional approval fee if the customer opted in
/** Phase 72: contribution from one CustomField's current value. Pure;
 *  the caller passes the map of all field values so we can skip hidden
 *  fields (those whose conditionalRule fails). */
export function customFieldDeltaCents(
  field: CustomField,
  value: string | string[] | undefined,
  allFieldValues: Record<string, string | string[]>,
): number {
  if (!evaluateCondition(field.conditionalRule, allFieldValues)) return 0;

  switch (field.priceImpactMode) {
    case "none":
      return 0;
    case "flat_when_set": {
      const set = Array.isArray(value)
        ? value.length > 0
        : typeof value === "string" && value.length > 0;
      return set ? field.priceImpactCents : 0;
    }
    case "per_char": {
      if (typeof value !== "string") return 0;
      return value.length * field.priceImpactCents;
    }
    case "per_selection": {
      // Multi-select (checkboxes): per ticked option, look up that
      // option's priceCents (falling back to field-level priceImpactCents).
      if (Array.isArray(value)) {
        return value.reduce((sum, v) => {
          const opt = field.options.find((o) => o.value === v);
          return sum + (opt?.priceCents ?? field.priceImpactCents);
        }, 0);
      }
      // Single-select (select / radio / swatch / image_swatch): one
      // option's priceCents, falling back to field-level priceImpactCents.
      if (typeof value === "string" && value.length > 0) {
        const opt = field.options.find((o) => o.value === value);
        return opt?.priceCents ?? field.priceImpactCents;
      }
      return 0;
    }
    default:
      return 0;
  }
}

/** Phase 72: sum every custom-field contribution. Called from
 *  totalCentsFor + by App.tsx's live-total display. */
export function customFieldsTotalCents(
  config: Config,
  fieldValues: Record<string, string | string[]>,
): number {
  const fields = config.customFields ?? [];
  let sum = 0;
  for (const f of fields) {
    sum += customFieldDeltaCents(f, fieldValues[f.slug], fieldValues);
  }
  return sum;
}

export function totalCentsFor(
  spots: Spot[],
  config: Config,
  approval: ApprovalChoice = null,
  /** Phase 22b: approval-step replacement upsell id. When set, the
   *  upsell's price applies instead of the standard approval fee. */
  approvalAddonId: string | null = null,
  /** Phase 37: order-level upsell IDs the customer ticked
   *  (Care kit, Gift box, Rush, etc.). Each priceCents stacks. */
  orderAddOnIds: string[] = [],
  /** Phase 72: admin-defined custom-field values keyed by slug. Hidden
   *  fields (conditional rule fails) contribute 0. */
  fieldValues: Record<string, string | string[]> = {},
): number {
  let cents = 0;
  spots.forEach((s, idx) => {
    const p = config.placements.find((pp) => pp.slug === s.placementSlug);
    if (!p) return;
    const tiers = effectiveTiersFor(p, config);
    cents += tierForSize(s.sizeMm, tiers, tiers.length > 0).feeCents;
    // Additional-placement ladder: first spot is free, subsequent spots
    // pick from Config.product.additionalPlacementFees.
    const nth = idx + 1;
    cents += additionalPlacementFeeFor(config, nth);
    // Phase 17c: the pet/kid scan upsell (addonUpsellId) IS this spot's
    // digitisation. Digitisation work is waived for an already-digitised
    // design — a reused design, or one matched by cross-order memory —
    // because the artwork is only ever digitised (and paid for) once.
    if (s.addonUpsellId) {
      const upsell = config.upsells?.find((u) => u.id === s.addonUpsellId);
      if (upsell) {
        // Phase 85: the scan upsell's BASE price is this spot's
        // digitisation - now charged via digitisationEventsFor() below
        // (first design free, additional designs charged), NOT here.
        // Phase 33b + 39c: per-count surcharge for extras beyond the
        // first (extraUploads.length on pet flows; legacy stepper else).
        // Per-count IS digitisation work (combining the extra subjects
        // into one design), so it shares the already-digitised waiver.
        if (!s.uploadAlreadyDigitised) {
          const effectiveCount =
            (s.extraUploads?.length ?? 0) > 0
              ? 1 + (s.extraUploads?.length ?? 0)
              : s.addonCount ?? 1;
          const perCount = effectiveUpsellPerCountCents(upsell, config);
          if (upsell.countMax && perCount && effectiveCount > 1) {
            const extra = Math.max(0, effectiveCount - 1);
            cents += extra * perCount;
          }
        }
        // Phase 33c: filled-style upgrade.
        // Phase 84: a locked "coloured" style STILL charges its surcharge.
        // Locking removes the customer's choice of style; it does NOT
        // waive the upgrade fee. (rk-pet-varsity proved that wrong -
        // silently undercharged $45 on the College Style Pet Crew.)
        // 2026-07-18 (RK24263): a REUSED design still charges it too.
        // The reuse waiver covers digitisation only; coloured + filled is
        // an artwork-quality upgrade stitched on EVERY garment, so it
        // must never ride the waiver. Third recurrence of this bug class
        // (promo zeroed it -> RK23901; lock skipped it -> Phase 84;
        // reuse skipped it -> RK24263). If a style should ever be free,
        // set styleSurchargeCents to 0 - never gate the charge.
        const styleSurcharge = effectiveUpsellStyleSurchargeCents(upsell, config);
        if (styleSurcharge && s.addonStyleColoured) {
          cents += styleSurcharge;
        }
      }
    }
    // Phase 33d: stackable extra upsells (sleeve emojis). Each one
    // adds its priceCents.
    if (s.extraAddonIds && s.extraAddonIds.length > 0) {
      for (const id of s.extraAddonIds) {
        const u = config.upsells?.find((x) => x.id === id);
        if (u) cents += u.priceCents;
      }
    }
    // 2026-08-14: "Match the design" threads = the coloured + filled fee,
    // once per spot, deduped against the add-on style surcharge above.
    // See matchDesignSurchargeCentsFor.
    cents += matchDesignSurchargeCentsFor(s, config);
  });
  // Phase 19 + 31: a logo skipped digitisation when the (email, hash)
  // pair was already paid for in a previous order OR the logo was
  // ingested from a verified past-order proof (both paths set
  // uploadAlreadyDigitised:true at upload time). A fresh upload that
  // doesn't match anything pays the fee — no trust-based bypass.
  // Phase 85: digitisation across every design (logo uploads + pet/kid
  // scans), with the FIRST design free. Single source of truth shared
  // with subtotalForSpot + the cart breakdown.
  for (const e of digitisationEventsFor(spots, config)) {
    if (!e.waived) cents += e.cents;
  }
  if (approval === "approve") {
    // Phase 22b: when the customer picked an approval-replacing upsell
    // (e.g. "Alternate colourway proof"), charge that upsell's price
    // INSTEAD of the standard approval fee — the upsell already covers
    // the proof workflow.
    if (approvalAddonId) {
      const upsell = config.upsells?.find((u) => u.id === approvalAddonId);
      cents += upsell ? upsell.priceCents : approvalFeeFor(config);
    } else {
      cents += approvalFeeFor(config);
    }
  }
  // Phase 37: order-level add-ons (Care kit, Gift box, Rush production).
  // Flat per-upsell surcharge; one of each maximum (toggle is binary in
  // the UI). Looked up at emit time so the customer can't pin a deleted
  // upsell into their cart.
  for (const id of orderAddOnIds) {
    const u = config.upsells?.find((x) => x.id === id);
    if (u) cents += u.priceCents;
  }
  // Phase 72: admin-defined custom-field contributions. Hidden fields
  // (conditional rule fails) and "none" pricing modes contribute 0.
  cents += customFieldsTotalCents(config, fieldValues);
  return cents;
}

// Per-spot total cents, for the saved-spot tile to render `+$N`. Doesn't
// dedupe digitisation across spots in the global sense (otherSpots passed
// in are the spots that came BEFORE this one, so the calc shows whether
// THIS spot's logo is fresh or recycled from an earlier one).
export function spotCentsFor(spot: Spot, config: Config, otherSpots: Spot[]): number {
  const p = config.placements.find((pp) => pp.slug === spot.placementSlug);
  if (!p) return 0;
  const tiers = effectiveTiersFor(p, config);
  let cents = tierForSize(spot.sizeMm, tiers, tiers.length > 0).feeCents;
  // Additional-placement fee for this spot's position in the saved list
  // (= its index in otherSpots-as-seen + 1). otherSpots is everything
  // BEFORE this one, so nth = otherSpots.length + 1.
  cents += additionalPlacementFeeFor(config, otherSpots.length + 1);
  // Phase 85: this spot's digitisation (logo fee OR pet/kid scan base),
  // with the first design per garment free. Build the event list across
  // the earlier spots + this one; charge only if this spot's event
  // exists and isn't the waived first. Reuse / already-digitised produce
  // no event, so $0.
  {
    const events = digitisationEventsFor([...otherSpots, spot], config);
    const thisEvent = events.find((e) => e.spot === spot);
    if (thisEvent && !thisEvent.waived) cents += thisEvent.cents;
  }
  // Phase 17c + 33b/c + 39c: the pet/kid scan upsell + count + style.
  // Digitisation work (base + per-count) is waived for an already-
  // digitised (reused) design — see totalCentsFor.
  // Phase 68: routed through effective-* helpers so promo zeroes them.
  if (spot.addonUpsellId) {
    const upsell = config.upsells?.find((u) => u.id === spot.addonUpsellId);
    if (upsell) {
      // Phase 85: the scan base IS digitisation, charged above via
      // digitisationEventsFor (first design free). Per-count is
      // digitisation work too, so it shares the reuse waiver.
      if (!spot.uploadAlreadyDigitised) {
        const effectiveCount =
          (spot.extraUploads?.length ?? 0) > 0
            ? 1 + (spot.extraUploads?.length ?? 0)
            : spot.addonCount ?? 1;
        const perCount = effectiveUpsellPerCountCents(upsell, config);
        if (upsell.countMax && perCount && effectiveCount > 1) {
          cents += Math.max(0, effectiveCount - 1) * perCount;
        }
      }
      // Phase 84 + RK24263 (2026-07-18): the coloured + filled surcharge
      // charges regardless of locks AND reuse - it's a per-garment
      // stitch upgrade, not digitisation. See totalCentsFor's note.
      const styleSurcharge = effectiveUpsellStyleSurchargeCents(upsell, config);
      if (styleSurcharge && spot.addonStyleColoured) {
        cents += styleSurcharge;
      }
    }
  }
  // Phase 33d: stackable extras.
  if (spot.extraAddonIds && spot.extraAddonIds.length > 0) {
    for (const id of spot.extraAddonIds) {
      const u = config.upsells?.find((x) => x.id === id);
      if (u) cents += u.priceCents;
    }
  }
  // 2026-08-14: "Match the design" threads = the coloured + filled fee,
  // once per spot, deduped against the add-on style surcharge above.
  cents += matchDesignSurchargeCentsFor(spot, config);
  return cents;
}

// Build the line-item properties payload for /cart/add.js.
//
// Visible properties (no underscore prefix) are shown to the customer in
// the cart UI + carried to the embroiderer's queue. Underscore-prefixed
// properties are hidden in cart but kept on the line.
//
// Phase 67u — Cart price uplift rides on a SEPARATE cart line. Folding
// the upcharge into one line needs a Cart Transform Function, and none
// is available: a custom app's function does not run on a non-Plus store
// (verified at checkout), and the Easify piggyback is dead (Easify's
// current version only prices customisation made through its own option
// UI). So App.tsx adds a second "Embroidery customisation" line carrying
// the exact upcharge, and the cart drawer groups the two lines visually.
// This function emits the descriptive "Spot N ..." properties plus the
// machine-readable `_added_by: rk-builder` / `_rk_price_delta_cents`
// that the orders/create webhook + BuilderOrder snapshot read.
export function propertiesFor(
  spots: Spot[],
  config: Config,
  approval: ApprovalChoice = null,
  customerNotes: string = "",
  approvalAddonId: string | null = null,
  /** Phase 37: order-level upsell IDs the customer ticked. */
  orderAddOnIds: string[] = [],
  /** Phase 51: worker base URL. When supplied we emit visible
   *  `Spot N design file` properties carrying the full /api/file?key=...
   *  URL — Shopify auto-renders URLs in line item properties as
   *  clickable links on the admin order page, so the embroidery team
   *  can open the uploaded file with one click. */
  workerUrl: string = "",
  /** Phase 72: admin-defined custom-field values keyed by slug. Each
   *  set field emits a `cartPropertyKey: <value>` property (or `_key`
   *  when cartPropertyVisible=false). Hidden fields are skipped. */
  fieldValues: Record<string, string | string[]> = {},
): Record<string, string> {
  const props: Record<string, string> = {};
  if (spots.length === 0) return props;

  const totalDelta = totalCentsFor(
    spots,
    config,
    approval,
    approvalAddonId,
    orderAddOnIds,
    fieldValues,
  );
  props["_added_by"] = "rk-builder";
  props["_rk_price_delta_cents"] = String(totalDelta);
  props["_spot_count"] = String(spots.length);
  props["Placements"] = String(spots.length);

  // Phase 67u: `pushTpo` is a no-op — pricing is applied by App.tsx's
  // separate "Embroidery customisation" cart line, not by cart-transform
  // markers. Kept as a no-op so the call sites scattered through
  // propertiesFor don't need touching.
  const pushTpo = (_label: string, _cents: number): void => {
    /* no-op */
  };

  // Approval choice. Visible to staff so they know whether to send a proof
  // before stitching, plus a hidden machine-readable flag. When the
  // customer picked an approval-replacing upsell, surface that on its
  // own property so staff know which workflow to run.
  const approvalAddon = approvalAddonId
    ? config.upsells?.find((u) => u.id === approvalAddonId)
    : null;
  // Phase 66: dropped the `_design_approval` and `_approval_addon_id`
  // hidden duplicates — nothing read them. The webhook reads the visible
  // "Design approval" property directly.
  if (approvalAddon) {
    props["Design approval"] = `${approvalAddon.label} (+$${(approvalAddon.priceCents / 100).toFixed(0)})`;
  } else if (approval === "approve") {
    props["Design approval"] = `Approve before stitching (+$${(approvalFeeFor(config) / 100).toFixed(0)})`;
  } else if (approval === "skip") {
    props["Design approval"] = "Skip, go straight to embroidery";
  }

  // Free-text customer notes. Trimmed so an accidental space-only entry
  // doesn't surface a blank property; capped here as a belt-and-braces
  // backup to the textarea's maxLength.
  //
  // Phase 66: dropped the `_customer_notes` hidden duplicate. Nothing
  // read it; the visible "Customer notes" property is the canonical
  // source.
  const notes = (customerNotes || "").trim().slice(0, 500);
  if (notes) {
    props["Customer notes"] = notes;
  }

  // Phase 85: which spots' digitisation is the free first design, so the
  // visible scan summary below shows "included" instead of "(+$30)" on
  // it (matching what totalCentsFor actually charges).
  const digiWaivedSpots = new Set(
    digitisationEventsFor(spots, config)
      .filter((e) => e.waived)
      .map((e) => e.spot),
  );

  spots.forEach((s, idx) => {
    const num = idx + 1;
    const p = config.placements.find((pp) => pp.slug === s.placementSlug);
    const placementName = p?.name ?? s.placementSlug ?? "";
    props[`Spot ${num} placement`] = placementName;
    props[`Spot ${num} size`] = `${s.sizeMm}mm`;
    // Phase 67i: when the customer pulled this design out of a past
    // order (verified-proof PDF or past-order spot lookup), stamp the
    // originating order on the new order so production sees the
    // provenance at a glance. Visible row for the admin + packing slip;
    // hidden duplicate so reuse-of-this-line keeps pointing back.
    // Reused / already-digitised design (2026-07-19): production must
    // know so no one redraws it. The VISIBLE row survives the lean cart
    // split onto the order line; the hidden flag travels in the DB bag
    // for the orders webhook, which tags the order `reused-design` and
    // appends a "do NOT redraw" line to the order note.
    if (s.sourceOrderName) {
      props[`Spot ${num} reused from`] = s.sourceOrderName;
      props[`_spot_${num}_source_order`] = s.sourceOrderName;
    } else if (s.reusedFromSpot) {
      // 2026-08-21: same-session reuse via the reuse tile - the design
      // is another spot ON THIS ORDER. Production digitises it once and
      // stitches it on both spots; there is no past-order file to find.
      props[`Spot ${num} reused from`] =
        `Spot ${s.reusedFromSpot} of this order (same design, digitise once)`;
    } else if (s.uploadAlreadyDigitised) {
      props[`Spot ${num} reused from`] =
        "a previous order (matched to an earlier upload)";
    }
    if (s.uploadAlreadyDigitised) props[`_spot_${num}_reused`] = "1";

    // Phase 46: emit Easify price-tpo deltas for this spot. Mirrors
    // totalCentsFor() so the cart-side total matches what the customer
    // saw in the price breakdown disclosure.
    if (p) {
      const tiers = effectiveTiersFor(p, config);
      const tierFee = tierForSize(s.sizeMm, tiers, tiers.length > 0).feeCents;
      pushTpo(`Spot ${num} ${placementName} ${s.sizeMm}mm`, tierFee);
    }
    pushTpo(
      `Spot ${num} additional-placement fee`,
      additionalPlacementFeeFor(config, num),
    );
    const font = s.fontId ? config.fonts.find((f) => f.id === s.fontId) : null;
    // Word Art (2026-07): the preset stands in for the font as the face.
    const wordArt = wordArtPresetById(s.wordArtStyleId);

    // Human-readable summary for the customer + embroiderer.
    // Phase 32b: when the font is uppercase-only, the visible summary
    // matches what'll actually stitch.
    //
    // Logo-only spots get no summary row — the single "Spot N design
    // file" link below IS the design, so a "Design (filename)" row was
    // just the same file named twice. Text / both modes still need the
    // row for the text content; "both" omits the filename because the
    // file link already carries it.
    const visibleText =
      font?.forceUppercase || wordArt?.forceUppercase
        ? s.text.toUpperCase()
        : s.text;
    const faceName = wordArt ? `Word Art · ${wordArt.name}` : font?.name;
    if (s.mode === "text") {
      props[`Spot ${num} design`] = faceName
        ? `Text "${visibleText}" in ${faceName}`
        : `Text "${visibleText}"`;
    } else if (s.mode === "both") {
      const arr = s.arrangement ?? "right";
      const textBit = faceName ? `"${visibleText}" in ${faceName}` : `"${visibleText}"`;
      props[`Spot ${num} design`] = `Text + design · ${textBit}, text ${arr} of design`;
    }

    // Hidden / structured for the embroiderer's downstream tooling.
    if (s.placementSlug) props[`_spot_${num}_slug`] = s.placementSlug;
    props[`_spot_${num}_mode`] = s.mode ?? "";
    props[`_spot_${num}_size_mm`] = String(s.sizeMm);
    // W4 phase 2 (on-garment preview): where the customer dragged the
    // design inside the calibrated photo zone, whole mm relative to the
    // zone centre ("x:<xMm>,y:<yMm>"). Position hint for production
    // ONLY - no pricing semantics, and omitted entirely when the design
    // was never moved (offset absent or rounds to centre).
    const previewOffsetProp = previewOffsetProperty(s.previewOffset);
    if (previewOffsetProp) {
      props[`_spot_${num}_preview_offset`] = previewOffsetProp;
    }
    if (s.mode === "logo" || s.mode === "both") {
      // Upload-later (2026-07-17): the customer chose "I'll send it
      // later". Hidden flag drives the orders/create webhook (order tag
      // + fulfilment hold + Klaviyo reminder); the visible row tells
      // production and the customer the artwork is coming via the
      // upload portal. A real staging key always wins over the flag.
      if (s.uploadLater && !s.uploadStagingKey) {
        props[`_spot_${num}_upload_later`] = "1";
        // Own key ("both" mode already uses "Spot N design" for the
        // text summary - don't clobber it).
        props[`Spot ${num} artwork`] =
          "Sending later - customer uploads after checkout";
      }
      if (s.uploadStagingKey) props[`_spot_${num}_upload_staging_key`] = s.uploadStagingKey;
      // Hidden filename. The reuse decoder + the orders/create webhook's
      // DigitisedLogo seeding both read `_spot_N_filename` — keeping it
      // underscore-prefixed keeps it off the order page. The visible
      // "Spot N design" summary already shows the filename in readable
      // form, so a separate visible filename row was pure duplication.
      if (s.uploadFilename) props[`_spot_${num}_filename`] = s.uploadFilename;
      // Phase 19: file hash for cross-order digitisation memory. Read on
      // orders/create to seed a DigitisedLogo row keyed by (email, hash).
      if (s.uploadFileHash) props[`_spot_${num}_upload_file_hash`] = s.uploadFileHash;
      // Phase 51 + 65: visible URL so the embroidery team can click
      // straight from the Shopify order detail.
      //
      // Shopify's admin auto-linkifies line-item property VALUES only
      // when the value is JUST a URL (starts with http:// or https://,
      // no leading or trailing text). Phase 51 included the filename in
      // the value ("logo.png — https://...") which broke linkification.
      //
      // The URL goes on its own property so Shopify renders it as a
      // clickable link. Skipped when workerUrl is empty (preview /
      // unit-test contexts).
      if (s.uploadStagingKey && workerUrl) {
        const base = workerUrl.replace(/\/$/, "");
        const url = `${base}/api/file?key=${encodeURIComponent(s.uploadStagingKey)}`;
        // Bare URL → Shopify admin renders it as <a href>.
        props[`Spot ${num} design file`] = url;
      }
      // Phase 39c: extra uploads on multi-pet designs. Each one is
      // emitted as _spot_N_extra_M_* (M is 1-indexed) so the embroidery
      // team can pull every file + the team's tooling knows there's
      // more than one source image to combine.
      if (s.extraUploads && s.extraUploads.length > 0) {
        s.extraUploads.forEach((eu, idx) => {
          const m = idx + 1;
          props[`_spot_${num}_extra_${m}_staging_key`] = eu.stagingKey;
          // Hidden filename — the reuse decoder reads `_spot_N_extra_M_filename`.
          // The visible "Spot N extra files" summary below already lists
          // every extra filename in readable form.
          if (eu.filename) props[`_spot_${num}_extra_${m}_filename`] = eu.filename;
          if (eu.fileHash) props[`_spot_${num}_extra_${m}_file_hash`] = eu.fileHash;
          // Phase 51 + 65: clickable URL for each extra upload. Bare URL
          // value so Shopify admin renders it as <a href>.
          if (workerUrl) {
            const base = workerUrl.replace(/\/$/, "");
            const url = `${base}/api/file?key=${encodeURIComponent(eu.stagingKey)}`;
            props[`Spot ${num} extra ${m} file`] = url;
          }
        });
        props[`Spot ${num} extra files`] = `${s.extraUploads.length} additional ${
          s.extraUploads.length === 1 ? "photo" : "photos"
        }: ${s.extraUploads.map((e) => e.filename).filter(Boolean).join(", ")}`;
      }
    }
    if (s.mode === "text" || s.mode === "both") {
      // Phase 32b: if the font has no lowercase glyphs (College, Stencil
      // Block) upper-case the cart text. The embroidery team won't have
      // to second-guess case + the customer's stitched output matches
      // what they saw in the preview.
      const fontForUpper = s.fontId
        ? config.fonts.find((f) => f.id === s.fontId)
        : null;
      const textOut =
        fontForUpper?.forceUppercase || wordArt?.forceUppercase
          ? s.text.toUpperCase()
          : s.text;
      if (textOut) props[`_spot_${num}_text`] = textOut;
      if (s.fontId) props[`_spot_${num}_font_id`] = s.fontId;
      // Word Art: the style id round-trips reuse; the Wilcom ref tells
      // production exactly which lettering treatment to punch.
      if (wordArt) {
        props[`_spot_${num}_wordart_style_id`] = wordArt.id;
        props[`_spot_${num}_wordart_ref`] = wordArt.embroideryRef;
      }
    }
    if (s.mode === "both" && s.arrangement) {
      props[`_spot_${num}_arrangement`] = s.arrangement;
    }

    // Phase 17c: replaces-step add-on on this spot (e.g. pet portrait).
    // Visible "Spot N add-on" for the embroidery team + hidden id for
    // round-trip decoding on reuse.
    if (s.addonUpsellId) {
      const upsell = config.upsells?.find((u) => u.id === s.addonUpsellId);
      if (upsell) {
        // Phase 46: tpo deltas for the add-on price + count surcharge +
        // style surcharge. Each emitted as a separate line so the cart
        // admin breakdown shows where every dollar comes from.
        pushTpo(`Spot ${num} ${upsell.label}`, upsell.priceCents);
        const effectiveCountForTpo =
          (s.extraUploads?.length ?? 0) > 0
            ? 1 + (s.extraUploads?.length ?? 0)
            : s.addonCount ?? 1;
        if (
          upsell.countMax &&
          upsell.perCountCents &&
          effectiveCountForTpo > 1
        ) {
          pushTpo(
            `Spot ${num} ${upsell.label} extras`,
            (effectiveCountForTpo - 1) * upsell.perCountCents,
          );
        }
        if (upsell.styleSurchargeCents && s.addonStyleColoured) {
          pushTpo(
            `Spot ${num} coloured + filled`,
            upsell.styleSurchargeCents,
          );
        }
        // Build a richer visible summary that includes count + style so
        // the embroidery team sees exactly what was picked without
        // cross-referencing the hidden id.
        const bits: string[] = [];
        // A reused design was already digitised + paid on the original,
        // so the scan upsell is free here — show that, don't claim "+$".
        const reused = !!s.uploadAlreadyDigitised;
        // Phase 85: the first design's digitisation is included; show
        // that rather than "(+$30)" so the label matches the charge.
        const scanWaived = digiWaivedSpots.has(s);
        bits.push(
          reused
            ? `${upsell.label} (reused, already digitised, no fee)`
            : scanWaived
              ? `${upsell.label} (first design, digitising included)`
              : `${upsell.label} (+$${(upsell.priceCents / 100).toFixed(0)})`,
        );
        // Phase 39c: prefer extraUploads.length for the visible
        // summary on pet flows; fall back to addonCount for legacy.
        const effectiveCount =
          (s.extraUploads?.length ?? 0) > 0
            ? 1 + (s.extraUploads?.length ?? 0)
            : s.addonCount ?? 1;
        if (upsell.countMax && upsell.countMax > 1 && effectiveCount > 1) {
          const extras = effectiveCount - 1;
          const extraCents = extras * (upsell.perCountCents ?? 0);
          bits.push(
            reused
              ? `${effectiveCount} subjects`
              : `${effectiveCount} subjects (+$${(extraCents / 100).toFixed(0)})`,
          );
        }
        if (upsell.styleSurchargeCents) {
          if (s.addonStyleColoured) {
            // RK24263: the coloured + filled fee charges on reused
            // designs too, so the label always shows the price.
            bits.push(
              `Coloured + filled (+$${((upsell.styleSurchargeCents) / 100).toFixed(0)})`,
            );
          } else if (s.addonStyleSingleLine) {
            // 2026-07-19: stylised single-stroke drawing (person flow,
            // free). The bit tells the artists which style to draw.
            bits.push("SINGLE LINE (one continuous stroke)");
          } else {
            bits.push("Line drawing");
          }
        }
        props[`Spot ${num} add-on`] = bits.join(" · ");
        props[`_spot_${num}_addon_id`] = upsell.id;
        if (s.addonCount && s.addonCount > 1) {
          props[`_spot_${num}_addon_count`] = String(s.addonCount);
        }
        if (upsell.styleSurchargeCents) {
          props[`_spot_${num}_addon_style`] = s.addonStyleColoured
            ? "coloured"
            : s.addonStyleSingleLine
              ? "single-line"
              : "line";
        }
        // 2026-07-19: person flow faces preference. Faceless is the
        // default house style; the visible row tells the artists which
        // way the customer went so nobody guesses, and the hidden flag
        // round-trips reuse.
        //
        // 2026-07-25: this used to bits.push() AFTER `Spot N add-on` had
        // already been joined on the line above, so the customer's choice
        // never reached the order at all - production could only guess.
        // It now gets its own visible row rather than being appended to
        // the add-on blob, so a face-bearing job is impossible to miss.
        if (flowKindFor(config) === "person") {
          props[`Spot ${num} faces`] = s.includeFaces
            ? "FACES INCLUDED (customer accepted the small-size note)"
            : "No faces (classic)";
          props[`_spot_${num}_faces`] = s.includeFaces ? "1" : "0";
        }
      }
    }
    // Phase 33d: visible "Spot N extras" line + hidden id list for the
    // sleeve emoji-stack add-ons (Heart, Paw, ...).
    if (s.extraAddonIds && s.extraAddonIds.length > 0) {
      const xs = s.extraAddonIds
        .map((id) => config.upsells?.find((u) => u.id === id))
        .filter((u): u is NonNullable<typeof u> => !!u);
      if (xs.length > 0) {
        props[`Spot ${num} extras`] = xs
          .map((u) => `${u.label} (+$${(u.priceCents / 100).toFixed(0)})`)
          .join(" + ");
        props[`_spot_${num}_extra_addon_ids`] = xs.map((u) => u.id).join(",");
        // Phase 46: tpo deltas, one per extra so the cart admin breakdown
        // shows each emoji/sleeve add-on individually.
        for (const u of xs) {
          pushTpo(`Spot ${num} ${u.label}`, u.priceCents);
        }
      }
    }


    // Thread colours. Visible to staff so the embroiderer knows what to
    // load on the machine; hidden CSV so the decoder can reconstruct the
    // exact selection if the customer reuses this design later.
    //
    // Only emit the thread this spot's mode actually uses: a logo-only
    // spot has no text, a text-only spot has no design. The builder
    // seeds a default text thread on every spot, so without this gate a
    // logo spot would carry a misleading "text thread" row.
    const threadLabel = (codes: string[]): string =>
      codes
        .map((c) => {
          if (c === MATCH_DESIGN_CODE) {
            return "Match the design (colour-matched at digitising)";
          }
          const t = config.threads.find((x) => x.code === c);
          return t ? `${t.name} (Isacord ${t.code})` : c;
        })
        .join(", ");
    const usesText = s.mode === "text" || s.mode === "both";
    const usesLogo = s.mode === "logo" || s.mode === "both";
    if (usesText && s.textThreadCodes.length > 0) {
      props[`Spot ${num} text thread`] = threadLabel(s.textThreadCodes);
      props[`_spot_${num}_text_thread_codes`] = s.textThreadCodes.join(",");
    }
    if (usesLogo && s.logoThreadCodes.length > 0) {
      props[`Spot ${num} design threads`] = threadLabel(s.logoThreadCodes);
      props[`_spot_${num}_logo_thread_codes`] = s.logoThreadCodes.join(",");
      // 2026-08-14: when the matched threads themselves carry the
      // coloured + filled fee (no add-on style surcharge covering this
      // spot), say so on its own visible row — support can reconcile the
      // $45 without decoding the delta, and production sees the stitch
      // style either way. Mirrors totalCentsFor exactly.
      const matchCents = matchDesignSurchargeCentsFor(s, config);
      if (matchCents > 0) {
        pushTpo(`Spot ${num} coloured + filled`, matchCents);
        props[`Spot ${num} coloured + filled`] =
          `Via matched threads (+$${(matchCents / 100).toFixed(0)})`;
      }
    }
    // Varsity / college: the arched text's stitched outline colour.
    if (s.outlineThreadCode) {
      props[`Spot ${num} text outline`] = threadLabel([s.outlineThreadCode]);
      props[`_spot_${num}_outline_thread_code`] = s.outlineThreadCode;
    }
  });

  // Phase 37: order-level add-ons (Care kit, Gift box, Rush production).
  // Visible row per upsell so the warehouse team can pull each one;
  // hidden CSV of ids for downstream tooling.
  if (orderAddOnIds.length > 0) {
    const picked = orderAddOnIds
      .map((id) => config.upsells?.find((u) => u.id === id))
      .filter((u): u is NonNullable<typeof u> => !!u);
    for (let i = 0; i < picked.length; i++) {
      const u = picked[i];
      props[`Add-on: ${u.label}`] = `+$${(u.priceCents / 100).toFixed(0)}`;
      // Phase 46: tpo delta per order-level add-on.
      pushTpo(`Add-on ${u.label}`, u.priceCents);
    }
    if (picked.length > 0) {
      props["_order_addon_ids"] = picked.map((u) => u.id).join(",");
    }
  }

  // Phase 46: digitisation fees — one per unique uploaded logo that hasn't
  // been pre-paid (cross-order memory). Mirrors totalCentsFor().
  const uniqueLogosForTpo = new Set<string>();
  for (const s of spots) {
    if (spotPaysStandardDigitisation(s)) {
      uniqueLogosForTpo.add(s.uploadStagingKey as string);
    }
  }
  const digitisationCents = digitisationFeeFor(config);
  let digiIdx = 0;
  for (const _key of uniqueLogosForTpo) {
    digiIdx++;
    pushTpo(
      uniqueLogosForTpo.size === 1
        ? "Digitisation fee"
        : `Digitisation fee ${digiIdx}`,
      digitisationCents,
    );
  }

  // Phase 46: approval fee or approval-replacing upsell.
  if (approval === "approve") {
    if (approvalAddonId) {
      const upsell = config.upsells?.find((u) => u.id === approvalAddonId);
      if (upsell) {
        pushTpo(`Approval ${upsell.label}`, upsell.priceCents);
      } else {
        pushTpo("Design approval", approvalFeeFor(config));
      }
    } else {
      pushTpo("Design approval", approvalFeeFor(config));
    }
  }

  // Phase 72: admin-defined custom-field values. Skip hidden fields
  // (conditional rule fails). select/radio/swatch/image_swatch values
  // serialise as their option's label (more useful on the order page
  // than the raw value). Checkboxes serialise as comma-separated labels.
  // Visibility (cartPropertyVisible) flips the property key from
  // visible "Whatever" to hidden "_whatever" so merchant-only fields
  // don't pollute the customer's cart summary.
  for (const f of config.customFields ?? []) {
    if (!evaluateCondition(f.conditionalRule, fieldValues)) continue;
    const v = fieldValues[f.slug];
    if (v == null) continue;
    let str: string;
    if (Array.isArray(v)) {
      if (v.length === 0) continue;
      str = v
        .map((opt) => f.options.find((o) => o.value === opt)?.label ?? opt)
        .join(", ");
    } else {
      if (!v) continue;
      str =
        f.fieldType === "select" ||
        f.fieldType === "radio" ||
        f.fieldType === "swatch" ||
        f.fieldType === "image_swatch"
          ? f.options.find((o) => o.value === v)?.label ?? v
          : v;
    }
    const key = f.cartPropertyVisible
      ? f.cartPropertyKey
      : `_${f.cartPropertyKey.replace(/^_+/, "")}`;
    props[key] = str;
  }

  // NOTE: propertiesFor returns the FULL property bag (visible +
  // underscore machine data). App.tsx splits it: the lean human-readable
  // subset rides on the garment cart line, and the full bag is persisted
  // to the BuilderDesign table (resolved by the orders/create webhook
  // via `_rk_group_id`). No JSON-blob property is attached — the
  // cart-transform merge keeps each component line's own properties, so
  // the brief survives onto the order without one.
  return props;
}

// Split the full property map produced by propertiesFor() into the lean
// set that actually rides on the Shopify cart line.
//
// Shopify shows EVERY line-item property in the admin order page —
// underscore prefix only hides a property from the *customer*, not the
// merchant. So the underscore-prefixed machine bag (staging keys, file
// hashes, addon ids, per-spot slugs/modes/threads, price delta) is
// persisted server-side in BuilderDesign instead, keyed by `_rk_group_id`.
//
// This keeps every human-readable property (no underscore prefix) plus
// the single `_rk_group_id` token that links the order line back to its
// BuilderDesign row, and drops everything else. The orders/create webhook
// + the reuse-from-order lookup re-hydrate the full bag from the DB.
//
// 2026-07-25 (security): `_rk_price_delta_cents` and `_rk_upload_pending`
// are now KEPT on the line as well as stored server-side. They are the two
// values the orders/create webhook makes money and fulfillment decisions
// from (underpayment guard, RK-UPLOAD-PENDING hold), and when the stored
// bag was their only home, one unauthenticated write to /api/save-design
// could zero them. Keeping a second copy on the immutable order line means
// the guard can take the higher/safer of the two. Both are underscore-
// prefixed so the customer never sees them; only the merchant does.
export function leanCartProperties(
  full: Record<string, string>,
): Record<string, string> {
  const KEEP = new Set([
    "_rk_group_id",
    "_rk_price_delta_cents",
    "_rk_upload_pending",
  ]);
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(full)) {
    if (!k.startsWith("_") || KEEP.has(k)) out[k] = v;
  }
  return out;
}

// Set of placement slugs that are locked for the *next* spot the customer
// picks. Computed from already-saved spots: each spot's placement plus all
// the placements that its `excludes` array names. Excludes the placement
// slug currently being edited (when editing an existing spot) so the
// customer can re-confirm their own pick.
export function lockedSlugsFor(
  spots: Spot[],
  placements: Placement[],
  editingSpotId: string | null,
): Map<string, string> {
  // slug → reason (which other spot triggered the lock; the placement name).
  const locked = new Map<string, string>();
  for (const s of spots) {
    if (s.id === editingSpotId) continue; // ignore the spot being re-edited
    if (!s.placementSlug) continue;
    const p = placements.find((pp) => pp.slug === s.placementSlug);
    if (!p) continue;
    // Lock the slug itself so you can't pick the same placement twice.
    if (!locked.has(s.placementSlug)) locked.set(s.placementSlug, p.name);
    for (const excl of p.excludes ?? []) {
      if (!locked.has(excl)) locked.set(excl, p.name);
    }
  }
  return locked;
}

// Unique prior uploads available for reuse from currently-saved spots. Used
// to render "Reuse <filename>" tiles above the drop-zone in subsequent
// spot editors.
export type PriorLogo = {
  stagingKey: string;
  filename: string;
  mimeType: string;
  /** Carried from the source spot so the reused draft keeps the Phase 19
   *  cross-order memory hash. */
  fileHash: string | null;
  /** Cross-order provenance carried from the source spot, so reusing a
   *  design that was itself reused still points at the original order. */
  sourceOrderName: string | null;
  fromSpotIndex: number;
};

export function priorLogosFrom(spots: Spot[]): PriorLogo[] {
  const seen = new Set<string>();
  const out: PriorLogo[] = [];
  spots.forEach((s, idx) => {
    if (!spotHasLogo(s) || !s.uploadStagingKey) return;
    if (seen.has(s.uploadStagingKey)) return;
    seen.add(s.uploadStagingKey);
    out.push({
      stagingKey: s.uploadStagingKey,
      filename: s.uploadFilename ?? s.uploadStagingKey,
      mimeType: s.uploadMimeType ?? "",
      fileHash: s.uploadFileHash ?? null,
      sourceOrderName: s.sourceOrderName ?? null,
      fromSpotIndex: idx + 1,
    });
  });
  return out;
}

